import React from "react";
import { cx } from "./cx";

/**
 * Button — Ed's variants from the Figma file (28 Sep call): tone pepper /
 * outline / flour / herb, size regular / large, icon left or right, a loading
 * state. Starter version written by Tom so pages can use it today; Ed owns the
 * styling and can rewrite the class strings freely. Keep the props stable —
 * pages depend on them.
 */
export type ButtonTone = "pepper" | "outline" | "flour" | "herb";
export type ButtonSize = "regular" | "large";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: ButtonTone;
  size?: ButtonSize;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  loading?: boolean;
  fullWidth?: boolean;
}

const tones: Record<ButtonTone, string> = {
  pepper: "bg-pepper text-salt border border-pepper hover:opacity-85",
  outline: "bg-transparent text-pepper border border-line hover:bg-flour",
  flour: "bg-flour text-pepper border border-flour hover:bg-ash",
  herb: "bg-herb text-salt border border-herb hover:opacity-85",
};

// 44px minimum touch target (platform readability floor).
const sizes: Record<ButtonSize, string> = {
  regular: "h-11 px-5 text-sm gap-2",
  large: "h-12 px-6 text-base gap-2",
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { tone = "pepper", size = "regular", iconLeft, iconRight, loading = false, fullWidth = false, disabled, className, children, type = "button", ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        "inline-flex items-center justify-center rounded-pill font-sans font-medium leading-none whitespace-nowrap cursor-pointer transition-opacity",
        "disabled:cursor-not-allowed disabled:opacity-50",
        tones[tone],
        sizes[size],
        fullWidth && "w-full",
        className
      )}
      {...rest}
    >
      {loading ? (
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" />
      ) : (
        iconLeft && <span className="inline-flex shrink-0" aria-hidden="true">{iconLeft}</span>
      )}
      <span>{children}</span>
      {!loading && iconRight && <span className="inline-flex shrink-0" aria-hidden="true">{iconRight}</span>}
    </button>
  );
});
